import { useState } from "react";
import { formatBackendEnumLabel, formatPlayerRoleLabel } from "../../../helper/helper";
import type { IBattingScoreCardItem, IBowlingScoreCardItem, ISubsitutes, ITeamsSquad } from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import { bowlerBalls, bowlerOvers, oversText, personName, strikeRate } from "./derive";
import { Avatar, Crest, Empty, Seg } from "./shared";
import type { Ctx } from "./shared";

const styleText = (v?: string | null) => {
  const s = (v ?? "").trim();
  return !s || s.toLowerCase() === "null" ? "" : formatBackendEnumLabel(s);
};

const roleTag = (role?: string | null) => {
  const r = (role ?? "").toUpperCase();
  if (r === "CAPTAIN" || r === "TEAM_CAPTAIN") return "C";
  if (r.includes("VICE")) return "VC";
  if (r.includes("WICKET")) return "WK";
  return "";
};

const roleLabel = (role?: string | null) => {
  const r = (role ?? "").toUpperCase();
  if (!r || r === "PLAYER" || r === "CAPTAIN" || r === "VICE_CAPTAIN" || r === "TEAM_CAPTAIN") return "";
  return formatPlayerRoleLabel(role);
};

const PlayersPane = ({ ctx }: { ctx: Ctx }) => {
  const { teams, inns, squads, winner } = ctx;
  const [role, setRole] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const query = q.trim().toLowerCase();

  const batOf = new Map<string, IBattingScoreCardItem>();
  const bowlOf = new Map<string, IBowlingScoreCardItem>();
  inns.forEach((i) => {
    i.batting.forEach((b) => batOf.set(b.playerId, b));
    i.bowling.forEach((b) => bowlOf.set(b.playerId, b));
  });

  const total = (squads[0]?.playingSquad?.length ?? 0) + (squads[1]?.playingSquad?.length ?? 0);

  const renderPlayer = (p: ITeamsSquad, i: number, color: string, soft: string) => {
    const name = personName(p.playerFirstName, p.playerLastName) || "Player";
    const bat = batOf.get(p.playerId);
    const bowl = bowlOf.get(p.playerId);
    const tag = roleTag(p.playerRole);
    const notOut = bat ? /not\s*out/i.test(bat.outString ?? "") || bat.status === "STRIKER" || bat.status === "NON_STRIKER" : false;
    const line = [roleLabel(p.playerRole), styleText(p.battingStyle), styleText(p.bowlingStyle)].filter(Boolean).join(" · ");
    const isOpen = open === p.playerId;
    return (
      <li key={p.playerId} className={`pr${isOpen ? " open" : ""}`}>
        <button type="button" className="prb" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : p.playerId)}>
          <span className="no">{i + 1}</span>
          <Avatar name={name} image={p.imageUri} color={color} soft={soft} />
          <span style={{ minWidth: 0 }}>
            <span className="nm" style={{ display: "block" }}>
              {name}
              {tag && <span className="tag">{tag}</span>}
              {ctx.potmId === p.playerId && <span className="tag star">POTM</span>}
            </span>
            {line && <span className="ro" style={{ display: "block" }}>{line}</span>}
          </span>
          <span className="perf">
            {bat ? <span className="p"><em>BAT</em>{bat.run}{notOut ? "*" : ""} <small>({bat.balls})</small></span> : bowl ? null : <span className="p x">Did not bat</span>}
            {bowl && <span className="p"><em>BOWL</em>{bowl.wickets ?? 0}/{bowl.runs ?? 0} <small>({bowlerOvers(bowl)})</small></span>}
          </span>
          <svg className="chev" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="pdet">
          <div className="pgrid">
            <div>
              Batting
              <b>{bat ? `${bat.run}${notOut ? "*" : ""} off ${bat.balls}, SR ${bat.strikeRate != null ? Number(bat.strikeRate).toFixed(1) : strikeRate(bat.run ?? 0, bat.balls ?? 0)}` : "Did not bat"}</b>
              {bat ? `${bat.fours ?? 0} fours, ${bat.six ?? 0} sixes. ${bat.outString || (notOut ? "Not out" : "")}` : ""}
            </div>
            <div>
              Bowling
              <b>{bowl ? `${bowlerOvers(bowl)}-${bowl.maiden ?? 0}-${bowl.runs ?? 0}-${bowl.wickets ?? 0}, economy ${bowl.economy != null ? Number(bowl.economy).toFixed(2) : bowlerBalls(bowl) ? ((bowl.runs ?? 0) / (bowlerBalls(bowl) / 6)).toFixed(2) : "-"}` : "Did not bowl"}</b>
              {bowl ? `${bowl.dotBall ?? 0} dot balls${bowl.wide ? `, ${bowl.wide} wide${bowl.wide > 1 ? "s" : ""}` : ""}` : ""}
            </div>
          </div>
          <div className="acts">
            <button type="button" className="btn primary" onClick={() => ctx.openPlayer(p.playerId)}>View player profile</button>
            <button type="button" className="btn" onClick={() => ctx.go("scorecard")}>See in scorecard</button>
          </div>
        </div>
      </li>
    );
  };

  const keep = (p: ITeamsSquad) => {
    if (query && !personName(p.playerFirstName, p.playerLastName).toLowerCase().includes(query)) return false;
    if (role === "bat") return batOf.has(p.playerId);
    if (role === "bowl") return bowlOf.has(p.playerId);
    return true;
  };

  if (!total) {
    return (
      <section className="pane on" id="pane-players" role="tabpanel" aria-labelledby="tab-players">
        <div className="box"><Empty>Squads are not available for this match.</Empty></div>
      </section>
    );
  }

  return (
    <section className="pane on" id="pane-players" role="tabpanel" aria-labelledby="tab-players">
      <div className="toolbar">
        <Seg label="Filter players" opts={[{ key: "all", label: `All ${total}` }, { key: "bat", label: "Batted" }, { key: "bowl", label: "Bowled" }]} cur={role} onPick={setRole} />
        <div className="r">
          <label className="search">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.6" /><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
            <input placeholder="Search players" aria-label="Search players" value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>
      </div>
      <div className="pteams">
        {teams.map((t, ti) => {
          const squad = squads[ti];
          const inn = inns.find((i) => i.team === t);
          const list = (squad?.playingSquad ?? []).filter(keep);
          const bench: ISubsitutes[] = squad?.bench ?? [];
          const won = winner === t;
          return (
            <div className="box" key={t.id || t.name}>
              <div className="box-h">
                <Crest team={t} className="chip-c" />
                <h3>{t.name}</h3>
                {winner && <span className={`res ${won ? "W" : "L"}`}>{won ? "W" : "L"}</span>}
                {inn && <span className="r"><span className="n" style={{ fontSize: 18, fontWeight: 700 }}>{inn.runs}/{inn.wkts}</span><span className="sub">{oversText(inn.legal)} ov</span></span>}
              </div>
              <div className="box-h" style={{ padding: "8px 18px", background: "#FBFBFC" }}>
                <span className="sub" style={{ fontWeight: 700 }}>Playing XI</span>
              </div>
              <ul className="pl">
                {list.length ? list.map((p, i) => renderPlayer(p, i, t.color, t.soft)) : <li className="empty">No players in this team match.</li>}
              </ul>
              {bench.length > 0 && (
                <div className="box-f">
                  <span style={{ fontWeight: 700, color: "var(--ink-2)" }}>Bench</span>
                  <span className="bench">{bench.map((b) => <span key={b.playerId}>{personName(b.playerFirstName, b.playerLastName)}</span>)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PlayersPane;
